import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Employee, EmployerSettings } from './types/Employee';

// Set up fonts for pdfMake
(pdfMake as any).vfs = pdfFonts;

/**
 * Generate Employment Contract PDF
 * Creates a legally compliant German employment contract (Arbeitsvertrag)
 */
export const generateEmploymentContractPDF = (
  employee: Employee,
  employerSettings: EmployerSettings
) => {
  // Helper functions
  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '[Datum eintragen]';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE');
  };

  const toNumber = (value: any): number => {
    if (value === null || value === undefined || value === '') return 0;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const getGenderSpecificText = (maleForm: string, femaleForm: string, diverseForm?: string): string => {
    if (!employee.gender) return diverseForm || maleForm;
    switch (employee.gender) {
      case 'm':
        return maleForm;
      case 'f':
        return femaleForm;
      case 'd':
        return diverseForm || maleForm;
      default:
        return maleForm;
    }
  };

  // Gender-specific terms
  const arbeitnehmer = getGenderSpecificText('Arbeitnehmer', 'Arbeitnehmerin', 'Arbeitnehmende');
  const nachfolgend = getGenderSpecificText('nachfolgend Arbeitnehmer genannt', 'nachfolgend Arbeitnehmerin genannt', 'nachfolgend Arbeitnehmende genannt');
  const die = getGenderSpecificText('Der', 'Die', 'Die');
  const der_die = getGenderSpecificText('der', 'die', 'die');
  const ihm = getGenderSpecificText('ihm', 'ihr', 'ihr');
  const er = getGenderSpecificText('er', 'sie', 'sie');

  // Contract data with fallbacks
  const contractStartDate = formatDate(employee.employment_start_date);
  const contractEndDate = employee.employment_end_date ? formatDate(employee.employment_end_date) : null;
  const jobTitle = employee.job_title || '[Berufsbezeichnung eintragen]';
  const jobDescription = employee.job_description || '[Tätigkeitsbeschreibung eintragen]';
  const workplaceLocation = employee.workplace_location || employerSettings.default_workplace_location || '[Arbeitsort eintragen]';

  // Calculate weekly hours from contracted_hours_per_month (assuming ~4.33 weeks/month)
  const weeklyHours = employee.contracted_hours_per_month
    ? Math.round(toNumber(employee.contracted_hours_per_month) / 4.33)
    : employee.working_days_per_week && employee.hours_per_day
      ? toNumber(employee.working_days_per_week) * toNumber(employee.hours_per_day)
      : 0;

  const workingDays = toNumber(employee.working_days_per_week) || '[X]';
  const hoursPerDay = toNumber(employee.hours_per_day) || '[X]';
  const hourlyRateText = employee.hourly_rate_comment
    ? `${employee.hourly_rate_comment} ${toNumber(employee.hourly_rate).toFixed(2)}€`
    : `${toNumber(employee.hourly_rate).toFixed(2)}€`;

  const vacationText = employee.vacation_days_per_year
    ? `${toNumber(employee.vacation_days_per_year)} Tage`
    : 'gemäß § 3 BUrlG';

  const contractTypeLine = employee.contract_type === 'befristet' && contractEndDate
    ? `Dieser Vertrag ist befristet bis zum ${contractEndDate}.`
    : 'Dieser Vertrag wird auf unbestimmte Zeit abgeschlossen.';

  const employeeName = `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || employee.Name;
  const employeeAddress = `${employee.street || ''}`;
  const employeeCity = `${employee.postal_code || ''} ${employee.city || ''}`.trim();

  // PDF Document Definition
  const docDefinition: any = {
    content: [
      // Title
      {
        text: 'ARBEITSVERTRAG',
        style: 'title',
        margin: [0, 0, 0, 10]
      },

      // Parties - Employer
      {
        text: 'Zwischen',
        margin: [0, 0, 0, 10]
      },
      {
        text: [
          { text: `${employerSettings.company_name}\n`, bold: true },
          `${employerSettings.owner_name}, ${employerSettings.owner_street}, ${employerSettings.owner_postal_code} ${employerSettings.owner_city}\n`
        ],
        margin: [0, 0, 0, 5]
      },
      {
        text: '-nachfolgend Arbeitgeber genannt -',
        italics: true,
        margin: [0, 0, 0, 10]
      },

      // Parties - Employee
      {
        text: 'und',
        margin: [0, 0, 0, 10]
      },
      {
        text: [
          { text: `${employeeName}\n`, bold: true },
          `${employeeAddress}\n`,
          `${employeeCity}\n`
        ],
        margin: [0, 0, 0, 5]
      },
      {
        text: `-${nachfolgend} -`,
        italics: true,
        margin: [0, 0, 0, 10]
      },

      {
        text: 'wird folgender Arbeitsvertrag geschlossen:',
        margin: [0, 0, 0, 12]
      },

      // § 1 - Duration
      {
        text: '§ 1 Dauer des Arbeitsverhältnisses',
        style: 'sectionHeader'
      },
      {
        text: `Beginn des Arbeitsverhältnisses ist der ${contractStartDate}`,
        margin: [0, 0, 0, 5]
      },
      {
        text: contractTypeLine,
        margin: [0, 0, 0, 10]
      },

      // § 2 - Activity
      {
        text: '§ 2 Tätigkeit',
        style: 'sectionHeader'
      },
      {
        text: [
          { text: `(1) ${die} ${arbeitnehmer} nimmt folgende Tätigkeit auf: `, margin: [0, 0, 0, 5] },
          { text: `${jobTitle}.\n`, bold: true },
          `Im Rahmen dieser Tätigkeit wird ${der_die} ${arbeitnehmer} mit folgenden Aufgaben beschäftigt:\n`,
          `${jobDescription}\n`
        ],
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Der Arbeitsort ist in ${workplaceLocation}.`,
        margin: [0, 0, 0, 10]
      },

      // § 3 - Working Hours
      {
        text: '§ 3 Arbeitszeit',
        style: 'sectionHeader'
      },
      {
        text: `(1) Die wöchentliche Arbeitszeit beträgt ${weeklyHours} Stunden.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) ${die} ${arbeitnehmer} arbeitet wöchentlich an ${workingDays} Arbeitstagen je ${hoursPerDay} Stunden reine Arbeitszeit.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: '(3) Pausen werden entsprechend der gesetzlichen Regelungen gewährt.',
        margin: [0, 0, 0, 5]
      },
      {
        text: '(4) Beginn und Ende der täglichen Arbeitszeit richtet sich nach der individuellen betrieblichen Einteilung, die für die tägliche Arbeitszeit erstellt wird.',
        margin: [0, 0, 0, 5]
      },
      {
        text: `(5) ${die} ${arbeitnehmer} hat an Sonn-und Feiertagen zu arbeiten, wenn der Arbeitgeber die Ankündigungsfrist von zwei Wochen einhält. Der Arbeitgeber behält sich die Anordnung von Überstunden unter der Berücksichtigung der betrieblichen Notwendigkeit und der berechtigten Interessen ${der_die}s ${arbeitnehmer}s vor. Die geleisteten Überstunden werden durch Freistellung ausgeglichen.`,
        margin: [0, 0, 0, 10]
      },

      // § 4 - Vacation
      {
        text: '§ 4 Urlaub',
        style: 'sectionHeader'
      },
      {
        text: `(1) ${die} ${arbeitnehmer} hat Anspruch auf Urlaub ${vacationText}.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Der Urlaub muss im laufenden Kalenderjahr vom Arbeitgeber gewährt und von ${der_die}m ${arbeitnehmer} genommen werden. Der Urlaub kann nur auf das Folgejahr übertragen werden, wenn eine schriftliche Vereinbarung vorliegt. Solle die Übertragung gewährt werden, muss der Urlaub bis zum 31. März des Folgejahres gewährt und genommen werden.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(3) Der vertraglich vorgesehen Urlaub mindert sich um 1/12 für jeden vollen Monat, in dem ${der_die} ${arbeitnehmer} keinen Anspruch auf Entgelt bzw. Entgeltfortzahlung hat.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: '(4) Der bis zur Beendigung des Arbeitsverhältnisses nicht vollständig in Natur gewährte gesetzliche Mindesturlaub, ist gemäß § 7 Abs. 4 BUrlG abzugelten.',
        margin: [0, 0, 0, 10]
      },

      // § 5 - Compensation
      {
        text: '§ 5 Arbeitsentgelt',
        style: 'sectionHeader'
      },
      {
        text: `(1) ${die} ${arbeitnehmer} erhält eine Vergütung in Höhe ${hourlyRateText} pro Stunde.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Sollte der Arbeitgeber irrtümlich den Betrag der Arbeitsvergütung übersteigen, hat ${der_die} ${arbeitnehmer} ${ihm} dies unverzüglich anzuzeigen und zu erstatten.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(3) Der Arbeitgeber hat die Möglichkeit freiwillig eine zusätzliche Zahlung (Sonderzahlung) zu gewähren. Die zusätzliche Zahlung begründet keinen Rechtsanspruch auf Sonderzahlungen für die Zukunft, auch wenn die Sonderzahlung wiederholt und ohne ausdrücklichen Vorbehalt der Freiwilligkeit erfolgte. Ein Anspruch auf Sonderzahlung besteht nicht, wenn das Arbeitsverhältnis ruht und kein Anspruch auf Arbeitsvergütung besteht. Diese Regelung gilt besonders für Elternzeit und unbezahlte Freistellung. Des Weiteren ist stets Voraussetzung für einen Anspruch auf Arbeitsvergütung, dass das Arbeitsverhältnis am Zahltag weder beendet noch gekündigt ist.`,
        margin: [0, 0, 0, 10]
      },

      // § 6 - Expense Reimbursement
      {
        text: '§ 6 Aufwendungsersatz',
        style: 'sectionHeader'
      },
      {
        text: `(1) Die Erstattung von Reisekosten, Spesen uns sonstige Aufwendungen, die ${der_die}m ${arbeitnehmer} aufgrund der Ausübung ${employee.gender === 'f' ? 'ihrer' : 'seiner'} Tätigkeiten nach diesem Arbeitsvertrag entstehen, werden ${der_die}m ${arbeitnehmer} vom Arbeitgeber erstattet, soweit eine internen Richtlinie besteht.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Die Erstattung dieser Kosten begrenzt sich auf die steuerlich zulässige Höchstgrenze. ${die} ${arbeitnehmer} hat dem Arbeitgeber in diesem Rahmen die Belege vorzulegen.`,
        margin: [0, 0, 0, 10]
      },

      // § 7 - Tax and Social Security
      {
        text: '§ 7 Steuer und Sozialversicherung',
        style: 'sectionHeader'
      },
      {
        text: 'Die Abgaben richten sich nach den gesetzlichen Vorgaben',
        margin: [0, 0, 0, 10]
      },

      // § 8 - Illness
      {
        text: '§ 8 Krankheit',
        style: 'sectionHeader'
      },
      {
        text: `(1) Sollte ${der_die} ${arbeitnehmer} infolge unverschuldeter Krankheit arbeitsunfähig sein, so besteht Anspruch auf Entgeltfortzahlung der Arbeitsvergütung bis zu einer Dauer von sechs Wochen nach den gesetzlichen Bestimmungen.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: '(2) Die Arbeitsverhinderung muss dem Arbeitgeber unverzüglich mitgeteilt werden.',
        margin: [0, 0, 0, 5]
      },
      {
        text: `(3) Sollte die Arbeitsverhinderung die Dauer von drei Kalendertagen überschreiten, hat ${der_die} ${arbeitnehmer} eine ärztliche Arbeitsunfähigkeitsbescheinigung über deren Bestehen und die voraussichtliche Dauer vorzulegen. Die Arbeitsunfähigkeitsbescheinigung ist dem Arbeitgeber spätestens an dem auf den dritten Kalendertag folgenden Arbeitstag vorzulegen gemäß § 5 Abs.1 EntgFG. Sollte die Arbeitsunfähigkeit die in der Bescheinigung angegebene Dauer überschreiten, ist ${der_die} ${arbeitnehmer} verpflichtet binnen drei Tagen eine Folgebescheinigung einzureichen.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(4) Diese Vorlegungspflicht gilt auch nach Ablauf der sechs Wochen. Der Arbeitgeber kann die Bescheinigung über die Arbeitsunfähigkeit ${der_die}s ${arbeitnehmer}s früher verlangen.`,
        margin: [0, 0, 0, 10]
      },

      // § 9 - Secondary Employment
      {
        text: '§ 9 Nebentätigkeit',
        style: 'sectionHeader'
      },
      {
        text: `${die} ${arbeitnehmer} ist dazu berechtigt einer weiteren geringfügigen Beschäftigung nachzugehen. Sollte ${der_die} ${arbeitnehmer} eine weitere geringfügige Beschäftigung aufnehmen wollen, ist ${er} dazu verpflichtet dem Arbeitgeber dies unverzüglich vor Beginn der weiteren Beschäftigung anzuzeigen. Sollte ${der_die} ${arbeitnehmer} diese Pflichten verletzen, hat ${er} dem Arbeitgeber den dadurch entstandenen Schaden zu ersetzen.`,
        margin: [0, 0, 0, 10]
      },

      // § 10 - Confidentiality
      {
        text: '§ 10 Verschwiegenheitspflicht',
        style: 'sectionHeader'
      },
      {
        text: `${die} ${arbeitnehmer} verpflichtet sich dazu, während der Dauer des Arbeitsverhältnisses und auch darüber hinaus Stillschweigen über Geschäfts-und Betriebsgeheimnisse, geschützte und personenbezogene Daten gemäß Bundesdatenschutzgesetz zu bewahren und zu keinem anderen als dem zur rechtmäßigen Aufgabenerfüllung gehörenden Zweck Informationen bekannt zu geben, zugänglich zu machen oder auf sonstige Art und Weise zu nutzen.`,
        margin: [0, 0, 0, 10]
      },

      // § 11 - Termination
      {
        text: '§ 11 Kündigung',
        style: 'sectionHeader'
      },
      {
        text: `(1) Das Arbeitsverhältnis dieses Arbeitsvertrages ist ${employee.contract_type === 'befristet' ? 'befristet' : 'unbefristet'}. Arbeitgeber und ${arbeitnehmer} sind dazu berechtigt den Vertrag ordentlich zu kündigen.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Es gelten die gesetzlichen Fristen über die Kündigung nach § 622 BGB. Das Arbeitsverhältnis ${der_die}s ${arbeitnehmer}s kann von den Parteien mit einer Frist von vier Wochen zum Fünfzehnten oder zum Ende des Kalendermonats gekündigt werden gemäß§ 622 Abs. 1 BGB. Die Verlängerung der Kündigungsfrist des Arbeitgebers und ${der_die}s ${arbeitnehmer}s richtet sich nach der Dauer des Arbeitsverhältnisses gemäß§ 622 Abs. 2 BGB.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(3) Der Arbeitgeber ist berechtigt, ${der_die} ${arbeitnehmer} bis zur Beendigung des Arbeitsverhältnisses freizustellen. Durch die Freistellung werden ${der_die}m ${arbeitnehmer} verbleibende Urlaubsansprüche und eventuelles Guthaben auf dem Arbeitszeitkonto angerechnet. Der durch ${employee.gender === 'f' ? 'ihre' : 'seine'} Arbeitskraft erzielte Verdienst wird dem Arbeitgeber auf den Vergütungsanspruch angerechnet.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: '(4) Mit Vollendung des gesetzlich festgelegt Renteneintrittsalters endet das Arbeitsverhältnis, es sei denn es wurde eine andere Vereinbarung diesbezüglich zwischen den Parteien geschlossen.',
        margin: [0, 0, 0, 5]
      },
      {
        text: `(5) Das unbefristete Arbeitsverhältnis endet mit Ablauf eines Monats, wenn der zuständige Sozialversicherungsträger ${der_die}s ${arbeitnehmer}s einen Bescheid zustellt, der die dauerhafte volle Erwerbsminderung oder Erwerbsunfähigkeit ${der_die}s ${arbeitnehmer}s feststellt. Dies gilt nicht, wenn ${der_die} ${arbeitnehmer} vor Ablauf der Widerspruchsfrist ${employee.gender === 'f' ? 'ihren' : 'seinen'} Antrag zurücknimmt oder ${employee.gender === 'f' ? 'ihren' : 'seinen'} Antrag auf Rente auf Zeit einschränkt. Des Weiteren hat ${der_die} ${arbeitnehmer} den Arbeitgeber über den Zugang eines solchen Bescheids zu informieren. Sollte ${der_die} ${arbeitnehmer} mit Zugang des Bescheids nicht sofort eine Rentenzahlung erhalten, so endet das Arbeitsverhältnis erst mit Ablauf des dem Rentenbeginn vorangehenden Tages. Sollte eine Rente auf Zeit gewährt werden, ruht das Arbeitsverhältnis für den Bewilligungszeitraum dieser Rente, längstens aber bis zum Beendigungszeitpunkt des Arbeitsverhältnisses.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: '(6) Die Kündigung bedarf der Schriftform und ist vor Antritt des Arbeitsverhältnisses ausgeschlossen.',
        margin: [0, 0, 0, 10]
      },

      // § 12 - Return of Company Property
      {
        text: '§ 12 Rückgabe von Betriebseigentum',
        style: 'sectionHeader'
      },
      {
        text: `${die} ${arbeitnehmer} hat bei Beendigung des Arbeitsverhältnisses alle Gegenstände an den Arbeitgeber herauszugeben, die ${ihm} im Rahmen ${employee.gender === 'f' ? 'ihres' : 'seines'} Arbeitsvertrages durch den Arbeitgeber oder Dritte zur Verfügung gestellt wurden. Hierzu gehören auch Daten und Datenträger, die in der Nutzungs- und Verfügungsbefugnis des Arbeitgebers stehen. Die Daten dürfen nur mit der vorherigen Zustimmung des Arbeitgebers gelöscht werden. Zurückbehaltungsrechte sind ausgeschlossen.`,
        margin: [0, 0, 0, 10]
      },

      // § 13 - Exclusion Periods
      {
        text: '§ 13 Verfall-und Ausschlussfristen',
        style: 'sectionHeader'
      },
      {
        text: '(1) Alle Ansprüche die sich aus dem Arbeitsvertrag begründen verfallen, wenn sie nicht innerhalb von drei Monaten nach Ablauf ihres Fälligkeitsdatums geltend gemacht werden. Ansprüche müssen dem Arbeitgeber gegenüber schriftlich geltend gemacht werden. Sollte der Arbeitgeber den Anspruch ablehnen, kann dieser innerhalb einer Frist von drei Monaten eingeklagt werden.',
        margin: [0, 0, 0, 5]
      },
      {
        text: '(2) Ansprüche die auf Handlungen wegen Vorsatz oder grober Fahrlässigkeit beruhen, bleiben von dieser Regelung unberührt.',
        margin: [0, 0, 0, 5]
      },
      {
        text: `(3) Die Ausschlussfrist ist nicht auf Ansprüche, die ${der_die} ${arbeitnehmer} im Bezug auf den gesetzlichen Mindestlohn fordert anwendbar, es sei denn der Vergütungsanspruch ${der_die}s ${arbeitnehmer}s geht über den Mindestlohn hinaus.`,
        margin: [0, 0, 0, 10]
      },

      // § 14 - Operating Agreements
      {
        text: '§ 14 Öffnungsklausel für Betriebsvereinbarungen',
        style: 'sectionHeader'
      },
      {
        text: 'Einzelne Rechte und Pflichten aus dem Arbeitsverhältnis können nach Abschluss des Arbeitsvertrages durch Betriebsvereinbarung geändert werden. Es gelten vom Zeitpunkt der Änderung an ausschließlich die jeweiligen Regelungen der Betriebsvereinbarung, auch wenn die, für den Mitarbeiter ungünstiger sind.',
        margin: [0, 0, 0, 10]
      },

      // § 15 - Contract Changes
      {
        text: '§ 15 Vertragsänderungen und Nebenabreden',
        style: 'sectionHeader'
      },
      {
        text: `(1) Durch das reine Verhalten des Arbeitgebers lassen sich keine Rechtsansprüche ${der_die}s ${arbeitnehmer}s begründen. Einvernehmliche Vertragsänderung müssen in schriftlicher Form abgegeben werden.`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `(2) Des Weiteren verpflichtet sich ${der_die} ${arbeitnehmer}, dem Arbeitgeber unverzüglich Veränderungen die ${employee.gender === 'f' ? 'ihre' : 'seine'} Person betreffen mitzuteilen. Dazu gehören beispielsweise Veränderungen ${employee.gender === 'f' ? 'ihres' : 'seines'} Familienstands und der Adresse.`,
        margin: [0, 0, 0, 20]
      },

      // Signatures
      {
        text: `Fockbek, ${formatDate(new Date().toISOString())}`,
        margin: [0, 0, 0, 25]
      },

      {
        columns: [
          {
            width: '*',
            stack: [
              { text: arbeitnehmer, margin: [0, 0, 0, 5] },
              { text: '————————————————', margin: [0, 0, 0, 0] }
            ]
          },
          {
            width: '*',
            stack: [
              { text: 'Arbeitgeber', margin: [0, 0, 0, 5] },
              { text: '————————————————', margin: [0, 0, 0, 0] }
            ]
          }
        ]
      }
    ],

    pageSize: 'A4',
    pageMargins: [50, 50, 50, 50],

    styles: {
      title: {
        fontSize: 16,
        bold: true,
        alignment: 'center'
      },
      sectionHeader: {
        fontSize: 10,
        bold: true,
        margin: [0, 8, 0, 6]
      }
    },

    defaultStyle: {
      fontSize: 8.5,
      lineHeight: 1.2
    }
  };

  const fileName = `Arbeitsvertrag_${employee.last_name || 'Mitarbeiter'}_${employee.first_name || ''}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdfMake.createPdf(docDefinition).download(fileName);
};
